import { cn } from '../../lib/utils'

interface LogoProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const sizes = {
  sm: { width: 100, height: 36 },
  md: { width: 140, height: 50 },
  lg: { width: 200, height: 72 },
}

/**
 * CNH PASS - Official Logo Component
 * Digital Ticket style with barcode
 */
export default function Logo({ size = 'md', className }: LogoProps) {
  const s = sizes[size]

  return (
    <svg
      width={s.width}
      height={s.height}
      viewBox="0 0 200 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('flex-shrink-0', className)}
    >
      <defs>
        <filter id="logoGlow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="0.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="logoGreenGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00B060" />
          <stop offset="100%" stopColor="#00C870" />
        </linearGradient>
        <linearGradient id="logoBorderGlow" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00B862" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#00D080" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#00B862" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Ticket shape */}
      <path
        d="M10 0 H190 Q200 0 200 10 V26 Q190 26 190 36 Q190 46 200 46 V62 Q200 72 190 72 H10 Q0 72 0 62 V46 Q10 46 10 36 Q10 26 0 26 V10 Q0 0 10 0 Z"
        fill="#101010"
      />

      {/* Border with glow */}
      <path
        d="M10 1 H190 Q199 1 199 10 V25.5 Q188 26 188 36 Q188 46 199 46.5 V62 Q199 71 190 71 H10 Q1 71 1 62 V46.5 Q12 46 12 36 Q12 26 1 25.5 V10 Q1 1 10 1 Z"
        fill="none"
        stroke="url(#logoBorderGlow)"
        strokeWidth="2"
      />

      {/* Inner border */}
      <path
        d="M10 3.5 H190 Q196.5 3.5 196.5 10 V24 Q184 25 184 36 Q184 47 196.5 48 V62 Q196.5 68.5 190 68.5 H10 Q3.5 68.5 3.5 62 V48 Q16 47 16 36 Q16 25 3.5 24 V10 Q3.5 3.5 10 3.5 Z"
        fill="none"
        stroke="#1a1a1a"
        strokeWidth="0.5"
      />

      {/* CNH text */}
      <text
        x="18"
        y="28"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="15"
        fontWeight="800"
        fill="#FFFFFF"
        letterSpacing="2"
      >
        CNH
      </text>

      {/* PASS text */}
      <text
        x="18"
        y="54"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="28"
        fontWeight="900"
        fill="url(#logoGreenGrad)"
        letterSpacing="3"
        filter="url(#logoGlow)"
      >
        PASS
      </text>

      {/* Separator */}
      <rect x="118" y="10" width="1" height="52" fill="#222" />

      {/* Barcode area */}
      <g transform="translate(126, 8)">
        <rect x="-4" y="-2" width="72" height="60" rx="4" fill="#0a0a0a" />
        <rect x="0" y="0" width="3" height="56" fill="#00B862" />
        <rect x="5" y="3" width="2" height="50" fill="#00B862" opacity="0.85" />
        <rect x="9" y="0" width="4" height="56" fill="#00B862" />
        <rect x="15" y="3" width="2" height="50" fill="#00B862" opacity="0.75" />
        <rect x="19" y="0" width="3" height="56" fill="#00B862" />
        <rect x="24" y="3" width="2" height="50" fill="#00B862" opacity="0.85" />
        <rect x="28" y="0" width="5" height="56" fill="#00B862" />
        <rect x="35" y="3" width="2" height="50" fill="#00B862" opacity="0.75" />
        <rect x="39" y="0" width="3" height="56" fill="#00B862" />
        <rect x="44" y="3" width="2" height="50" fill="#00B862" opacity="0.85" />
        <rect x="48" y="0" width="4" height="56" fill="#00B862" />
        <rect x="54" y="3" width="2" height="50" fill="#00B862" opacity="0.75" />
        <rect x="58" y="0" width="3" height="56" fill="#00B862" />
      </g>
    </svg>
  )
}

/**
 * CNH PASS Icon - For favicon, app icons
 */
export function LogoIcon({ size = 48, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('flex-shrink-0', className)}
    >
      <defs>
        <linearGradient id="iconBgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00B862" />
          <stop offset="100%" stopColor="#00D080" />
        </linearGradient>
        <filter id="iconGlow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="0.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Ticket shape background */}
      <path
        d="M6 0 H42 Q48 0 48 6 V17 Q42 17 42 24 Q42 31 48 31 V42 Q48 48 42 48 H6 Q0 48 0 42 V31 Q6 31 6 24 Q6 17 0 17 V6 Q0 0 6 0 Z"
        fill="url(#iconBgGrad)"
      />

      {/* Inner dark area */}
      <rect x="8" y="8" width="32" height="32" rx="4" fill="#0a0a0a" />

      {/* P letter */}
      <text
        x="24"
        y="32"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="24"
        fontWeight="900"
        fill="#00B862"
        textAnchor="middle"
        filter="url(#iconGlow)"
      >
        P
      </text>

      {/* Mini barcode */}
      <g transform="translate(30, 12)" opacity="0.7">
        <rect x="0" y="0" width="2" height="16" fill="#00B862" />
        <rect x="4" y="2" width="1" height="12" fill="#00B862" />
        <rect x="7" y="0" width="2" height="16" fill="#00B862" />
      </g>
    </svg>
  )
}

/**
 * Horizontal inline logo for headers
 */
export function LogoInline({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <LogoIcon size={36} />
      <div className="flex flex-col">
        <span className="font-bold text-white text-sm leading-tight">CNH PASS</span>
        <span className="text-[10px] text-white/50 leading-tight">Sua aprovacao garantida</span>
      </div>
    </div>
  )
}
